import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '@components/ui/button';
import { SchedulerShell } from '@components/scheduling/shell';
import { ScheduleDialog } from '@components/rescheduler/dialog';
import { getJobSubtitle } from '@components/schedules/dialogs/internal/helpers';
import { useRescheduler } from '@contexts/rescheduler';
import { initialState } from '@contexts/rescheduler/constants';
import { useToast } from '@hooks/useToast';
import { toTenantDate } from '@utils/timeZone';
import { cn } from '@utils/cn';
import { Schedule } from '@root/globalTypes';
import { RelatedCard } from './card';
import { allDecided, dayLabel, getShiftDays } from './helpers';
import { RelatedDecision, RelatedResult, RelatedRunner } from './types';

const ALL: RelatedDecision[] = ['move', 'keep', 'remove'];

export const PhoneRelatedSchedules = () => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.phone.related' });
  const { t: tApp } = useTranslation('app');
  const { toast } = useToast();
  const { open, relatedSchedules, schedule, movedFrom, savedSchedules, setState, isSavedSchedule } =
    useRescheduler();
  const [decisions, setDecisions] = useState<Record<string, RelatedDecision>>({});
  const [expanded, setExpanded] = useState<string>();
  const [results, setResults] = useState<Record<string, RelatedResult>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [applying, setApplying] = useState(false);
  const [editing, setEditing] = useState<Schedule>();
  const runners = useRef<Record<string, RelatedRunner>>({});
  const visible = open && relatedSchedules.length > 0;
  const shiftDays = getShiftDays(schedule.startsAt, movedFrom);

  useEffect(() => {
    if (visible) return;
    setDecisions({});
    setExpanded(undefined);
    setResults({});
    setErrors({});
    setEditing(undefined);
  }, [visible]);

  useEffect(() => {
    if (!savedSchedules.length) return;
    setDecisions((prev) => {
      const next = { ...prev };
      relatedSchedules.forEach((el) => {
        if (isSavedSchedule(el) && next[el.id] !== 'remove') next[el.id] = 'keep';
      });
      return next;
    });
  }, [savedSchedules]);

  const register = useCallback((id: string, runner?: RelatedRunner) => {
    if (runner) runners.current[id] = runner;
    else delete runners.current[id];
  }, []);

  if (!visible) return null;

  const decisionOf = (el: Schedule) => decisions[el.id] || 'move';
  const pending = relatedSchedules.filter((el) => !results[el.id]);
  const close = () => setState(initialState);

  const decide = (list: Schedule[], value: RelatedDecision) => {
    setDecisions((prev) => ({ ...prev, ...Object.fromEntries(list.map((el) => [el.id, value])) }));
    setErrors((prev) => {
      const next = { ...prev };
      list.forEach((el) => delete next[el.id]);
      return next;
    });
    if (value !== 'move' && list.some((el) => el.id === expanded)) setExpanded(undefined);
  };

  const apply = async () => {
    const targets = pending.filter((el) => decisionOf(el) !== 'keep');
    if (!targets.length) {
      close();
      return;
    }
    setApplying(true);
    setErrors({});
    const outcomes = await Promise.all(
      targets.map(async (el) => {
        const decision = decisionOf(el);
        const runner = runners.current[el.id]?.[decision === 'remove' ? 'remove' : 'move'];
        const outcome = runner ? await runner() : { ok: false };
        return { el, decision, outcome };
      }),
    );
    setApplying(false);
    const failed = outcomes.filter((el) => !el.outcome.ok);
    setResults((prev) => ({
      ...prev,
      ...Object.fromEntries(
        outcomes
          .filter((el) => el.outcome.ok)
          .map((el) => [el.el.id, el.decision === 'remove' ? 'removed' : 'moved']),
      ),
    }));
    if (!failed.length) {
      toast({ title: t('applied') });
      close();
      return;
    }
    setErrors(
      Object.fromEntries(
        failed.map(({ el, decision, outcome }) => [
          el.id,
          t(
            'error' in outcome && outcome.error
              ? outcome.error
              : decision === 'remove'
                ? 'removeFailed'
                : 'saveFailed',
          ),
        ]),
      ),
    );
    const firstSave = failed.find((el) => el.decision === 'move');
    if (firstSave) setExpanded(firstSave.el.id);
  };

  const fromDate = movedFrom ? toTenantDate(movedFrom) : undefined;
  const toDate = schedule.startsAt ? toTenantDate(schedule.startsAt) : undefined;
  const editingTask = editing?.task;

  return (
    <>
      <SchedulerShell
        open={visible}
        onOpenChange={(value) => !value && close()}
        title={t('title')}
        subtitle={getJobSubtitle(relatedSchedules[0]) || getJobSubtitle(schedule as Schedule)}
        toolbar={
          <div className="flex flex-col gap-2 border-b border-neutral-100 bg-neutral-50/60 px-4 py-3">
            <p className="text-[15px] leading-snug text-neutral-600">
              {t('intro', { name: schedule.task?.name })}
            </p>
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[17px] font-bold text-neutral-800">
                {schedule.task?.name}
              </span>
              {fromDate && shiftDays !== 0 && (
                <>
                  <span className="shrink-0 text-[15px] text-neutral-400 line-through">
                    {dayLabel(fromDate)}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-neutral-500" />
                </>
              )}
              {toDate && (
                <span className="shrink-0 rounded-full bg-primary-50 px-3 py-1 text-[15px] font-bold text-primary-600">
                  {dayLabel(toDate)}
                </span>
              )}
            </div>
          </div>
        }
        footer={
          <div className="grid grid-cols-2 gap-2 [&_button]:h-11 [&_button]:w-full [&_button]:justify-center">
            <Button variant="secondary" disabled={applying} onClick={close}>
              {tApp('actions.cancel')}
            </Button>
            <Button
              disabled={applying}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={apply}>
              {t('apply')}
            </Button>
          </div>
        }>
        <div className="flex flex-col gap-3 bg-white px-4 pb-4 pt-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[17px] font-bold text-neutral-800">
              {t('count', { count: relatedSchedules.length })}
            </h3>
            {pending.length > 1 && (
              <span className="text-sm text-neutral-500">{t('applyToAll')}</span>
            )}
          </div>
          {pending.length > 1 && (
            <div className="grid grid-cols-3 gap-2">
              {ALL.map((value) => {
                const active = allDecided(pending, decisionOf, value);
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    disabled={applying}
                    onClick={() => decide(pending, value)}
                    className={cn(
                      'h-12 rounded-lg border-[1.5px] text-[15px] disabled:opacity-60',
                      active
                        ? 'border-primary-500 bg-primary-50 font-semibold text-primary-600'
                        : 'border-neutral-50 bg-neutral-50 text-neutral-500',
                    )}>
                    {t(`${value}All`)}
                  </button>
                );
              })}
            </div>
          )}
          {relatedSchedules.map((el) => (
            <RelatedCard
              key={el.id}
              data={el}
              shiftDays={shiftDays}
              decision={decisionOf(el)}
              expanded={expanded === el.id}
              result={results[el.id]}
              error={errors[el.id]}
              updated={isSavedSchedule(el)}
              disabled={applying || Boolean(results[el.id])}
              onDecision={(value) => decide([el], value)}
              onToggle={() => setExpanded(expanded === el.id ? undefined : el.id)}
              onEdit={() => setEditing(el)}
              register={register}
            />
          ))}
        </div>
      </SchedulerShell>
      {editingTask && (
        <ScheduleDialog
          open={Boolean(editing)}
          setOpen={(value) => !value && setEditing(undefined)}
          phoneLayout
          nested
          task={{ ...editingTask, extraComment: editing.extraComment, schedule: editing }}
          job={{
            id: editingTask.job?.id || '',
            projectId: editingTask.job?.projectId || '',
            name: editingTask.job?.name || '',
            address: editingTask.job?.address || '',
            division: editingTask.job?.division || { id: '', name: '' },
            project: editingTask.job?.project
              ? {
                  name: editingTask.job.project.name || '',
                  address: editingTask.job.project.address || '',
                }
              : undefined,
          }}
        />
      )}
    </>
  );
};
